import type { FeatureCollection } from 'geojson'
import { V_FLAG_COMPONENTS } from '@vartion/ui'
import { acceptHMRUpdate, defineStore } from 'pinia'

export interface Country {
  name: string
  alpha_2: string
  alpha_3: string
  aliases: string[]
  capital: string
  subdivision_names: string[]
}

export const useCountries = defineStore('countries', () => {
  const countries = shallowRef<Record<string, Country>>({})

  const geoJSON = shallowRef<FeatureCollection>({
    features: [],
    type: 'FeatureCollection',
  })

  const countriesByAlpha2Code = computed(() => keyBy('alpha_2', Object.values(countries.value), (alpha2) => alpha2.toLowerCase()))
  const countriesByAlpha3Code = computed(() => keyBy('alpha_3', Object.values(countries.value), (alpha3) => alpha3.toLowerCase()))
  const countriesByCapital = computed(() => keyBy('capital', Object.values(countries.value), (capital) => capital.toLowerCase()))

  const richFlagStyles = computed(() => {
    const rich = {} as Record<string, any>
    for (const country of Object.values(countries.value)) {
      rich[country.alpha_3] = {
        height: 20,
        width: 30,
        align: 'left',
        backgroundColor: {
          image: V_FLAG_COMPONENTS[country.alpha_2.toUpperCase() as keyof typeof V_FLAG_COMPONENTS],
        },
      }
    }

    return rich
  })

  function country(name: string): Country | null {
    if (!name) {
      return null
    }

    name = name.toLowerCase()
    if (name in countries.value) {
      return countries.value[name]
    }

    if (name in countriesByAlpha2Code.value) {
      return countriesByAlpha2Code.value[name]
    }

    if (name in countriesByAlpha3Code.value) {
      return countriesByAlpha3Code.value[name]
    }

    if (name in countriesByCapital.value) {
      return countriesByCapital.value[name]
    }

    for (const key in countries.value) {
      const country = countries.value[key]
      if (country.subdivision_names.includes(name)) {
        return country
      }
      if (country.aliases.includes(name)) {
        return country
      }
    }

    return null
  }

  function countryName(value: string): string | null {
    return country(value)?.name ?? null
  }

  function queryCountries(query: string, cb?: (results: { value: string; code: string }[]) => void) {
    if (!query) {
      const results: { value: string; code: string }[] = []
      for (const key in countries.value) {
        const country = countries.value[key]
        results.push({ value: country.name, code: country.alpha_2 })
      }
      if (cb) {
        return cb(results)
      }

      return results
    }

    query = query.toLowerCase()

    const results = []
    for (const [key, country] of Object.entries(countries.value)) {
      const result = { value: country.name, code: country.alpha_2 }

      if (key.includes(query)) {
        results.push(result)
      } else if (country.alpha_3.toLowerCase().includes(query)) {
        results.push({ ...result, label: country.alpha_3 })
      } else if (country.capital.toLowerCase().includes(query)) {
        results.push({ ...result, label: country.capital })
      }
    }

    if (cb) return cb(results)

    return results
  }

  function searchGeoJSON(name: string) {
    return geoJSON.value.features.filter((feature) => feature.properties?.name === name)
  }

  async function fetchGeoJSON() {
    if (Object.keys(geoJSON.value.features).length > 0) {
      return
    }

    const { data } = await web.get<FeatureCollection>(`countries.geojson?${$env.version}`)
    geoJSON.value = data
  }

  async function fetchCountries() {
    const { data } = await api.get('countries')
    countries.value = data
  }
  fetchCountries()

  return {
    countries,
    countriesByAlpha2Code,
    countriesByAlpha3Code,
    geoJSON,
    country,
    countryName,
    queryCountries,
    richFlagStyles,
    searchGeoJSON,
    fetchGeoJSON,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCountries, import.meta.hot))
}
